import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiAutoFix, mdiCheck, mdiClose, mdiLoading, mdiMessageOutline, mdiRestore, mdiRobotOutline, mdiTicketOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { DetailSection } from './detail-section'
import { KnowledgeFactLink, NoteWrongButton, parseKnowledgeSource } from '../knowledge/KnowledgeFact'
import { useFormatters } from '../../lib/format'
import type { Conversation, ConversationMessage, ConversationStatus } from '../../api/schema'
import { threadTitle } from './thread'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * The triage card (spec 2026-09-11-06 § 3).
 *
 * It sits above Context in the thread's details column and renders the
 * proposal the `message_triage` job wrote for the latest inbound message: the
 * outcome, the urgency, the request candidates, the facts the draft rests on,
 * and the draft itself.
 *
 * Three rules shape it, and each of them is a refusal:
 *
 *   - it is ALWAYS there, and when it has no proposal it says why, in one grey
 *     line (spec 2026-09-16-01): a deployment with no LLM key, a thread with no
 *     anchor, a message with no words, a thread the resident has not written to
 *     yet. A card that vanishes reads as a feature that is broken; a card that
 *     names its reason reads as a feature that declined. *Run again* is offered
 *     only for the reasons that can change on their own.
 *   - nothing happens without a click. Accept creates the tickets, Edit opens
 *     the promote form pre-filled, Reject records that a human said no — and
 *     none of the three sends anything.
 *   - **the draft is applied to the composer on request only.** It lives here,
 *     read-only, and *Use this draft* copies it across; from that moment the
 *     text is the manager's, autosaved like anything they typed. The row keeps
 *     its own copy, so a wrong edit can be undone by applying it again.
 *     Accept counts as such a request when — and only when — the composer is
 *     empty: accepting a proposal that carries a draft is accepting its answer,
 *     and making that a second click on *Use this draft* was busywork. It never
 *     overwrites: a composer holding text keeps it, and the draft stays in the
 *     card.
 */

/** What the card can do to the composer, without owning its state. */
export type ComposerApi = {
    /** Whether the composer currently holds text a manager would lose. */
    hasText: () => boolean
    /**
     * Copies a draft in and focuses the body.
     *
     * `rememberPrevious` keeps whatever was being replaced and makes the
     * composer say so underneath, with *show* and *delete* (spec
     * 2026-09-16-09). Opt-in per call: the triage card does not pass it, and
     * its behaviour is unchanged — its own draft stays one click away on the
     * card, so the composer has nothing to remember.
     */
    applyDraft: (subject: string, body: string, options?: { rememberPrevious?: boolean; triageUid?: string }) => void
    /** Appends text after what is typed (a knowledge note's *Insert*, spec 2026-11-01-01). */
    insertText?: (text: string) => void
}

type TriageRow = NonNullable<ConversationMessage['triage']>
type TriageRequest = NonNullable<TriageRow['requests']>[number]

/** Same shape of tone as the ticket queue's, so an outcome reads at a glance. */
const OUTCOME_TONE: Record<string, string> = {
    answer: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
    ticket: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
    answer_and_ticket: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
    acknowledge: 'bg-muted text-muted-foreground border-border',
    escalate: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
}

const URGENCY_TONE: Record<string, string> = {
    low: 'text-muted-foreground',
    normal: 'text-muted-foreground',
    high: 'text-amber-700 dark:text-amber-400',
}

/**
 * The skip reasons a re-run can change: the model was never asked, or answered
 * nothing, for a reason that is about the deployment or the day rather than
 * about the message. The structural ones — no site, no words, an auto-responder
 * — are not offered a retry: it would skip again for the same reason, and the
 * line says what unblocks them instead.
 */
const RETRYABLE_SKIPS = new Set(['no_api_key', 'api_key_rejected', 'empty_answer', 'daily_cap'])
const KNOWN_SKIPS = new Set([
    ...RETRYABLE_SKIPS,
    'no_anchor',
    'empty_body',
    'auto_submitted',
    'outbound',
    'message_gone',
])

/** What the manager can seed the promote form with, for the Edit path. */
export type PromoteSeed = {
    title: string
    subtype: string
    assetUid: string
    assetName: string
    /**
     * The message whose proposal is being corrected. Its presence is what turns
     * the promote form's submit into a review recorded as `edited` — so the
     * acceptance history keeps "a human had to change it" distinct from "a human
     * opened a ticket by hand".
     */
    reviewMessageUid: string
}

export function TriageSection({
    org,
    conversation,
    composerApi,
    onEditProposal,
    onChanged,
    onStatusChanged,
}: {
    org: string
    conversation: Conversation
    composerApi: React.MutableRefObject<ComposerApi | null>
    onEditProposal: (seed: PromoteSeed) => void
    onChanged: () => void
    onStatusChanged?: (next: ConversationStatus) => void
}) {
    const { t } = useTranslation('conversations')
    const { formatDate } = useFormatters()
    const [error, setError] = useState<string | null>(null)
    const [confirmReplace, setConfirmReplace] = useState<{ subject: string; body: string } | null>(null)

    // `latest` is the latest inbound message: the one a manager is answering,
    // and whose `triage_status` says whether a run is outstanding. An older
    // message's proposal has already had its turn as the thing being answered
    // — but it may still be the only proposal there is (spec 2026-09-14-06).
    const latest = useMemo(() => {
        const messages = (conversation.messages ?? []) as ConversationMessage[]
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            if (messages[i].direction === 'inbound') return messages[i]
        }

        return undefined
    }, [conversation.messages])

    // `proposal` is the most recent inbound message that still carries a
    // CURRENT triage row: `latest`'s own row when it has one, else the
    // closest earlier inbound message's. A retriage on `latest` keeps its old
    // row live while the new run is outstanding (`ApplyTriage` only supersedes
    // in the same transaction as the row that replaces it); a fresh inbound
    // message starts with none of its own and must not hide the proposal
    // still standing on an earlier one — that hiding is the bug this spec
    // fixes.
    const proposalEntry = useMemo(() => {
        const messages = (conversation.messages ?? []) as ConversationMessage[]
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            const candidate = messages[i]
            if (candidate.direction !== 'inbound') continue
            const row = candidate.triage as TriageRow | undefined | null
            if (row) return { message: candidate, triage: row }
        }

        return undefined
    }, [conversation.messages])

    const review = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/messages/{messageUid}/triage/review',
        {
            onSuccess: (data: unknown) => {
                setError(null)
                onChanged()
                // `acknowledge` closes the thread, which may take it out of the
                // folder on screen — the queue advances its selection on exactly
                // this signal (spec 2026-09-10-06).
                if ((data as { conversation_closed?: boolean } | undefined)?.conversation_closed) {
                    onStatusChanged?.('closed')
                }
            },
            onError: (e: unknown) => {
                const body = e as { code?: string; title?: string } | undefined
                // 409: a retriage replaced this proposal, or another manager
                // decided it first. Refetching is the fix, not retrying.
                setError(body?.code === 'CONFLICT' ? t('triage.stale') : t('triage.review_failed'))
                onChanged()
            },
        },
    )

    const retriage = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/messages/{messageUid}/triage',
        {
            onSuccess: () => {
                setError(null)
                onChanged()
            },
            onError: () => setError(t('triage.retry_failed')),
        },
    )

    const triage = proposalEntry?.triage ?? null
    const proposalMessage = proposalEntry?.message
    // No inbound message yet (a manager-first thread): nothing to analyse, and
    // the card says so instead of vanishing. `status` then reads `pending` by
    // default, but `refreshing` is what gates the spinner, and it needs a
    // message.
    const status = latest?.triage_status ?? 'pending'
    const refreshing = latest != null && status === 'pending'
    // `skipped` carries WHY on the message. An older row, or one the shared
    // key gate marked before the column existed, carries nothing and gets the
    // generic line — with a retry, since nothing says it would skip again.
    const skipReason = String(latest?.triage_skip_reason ?? '')
    const notAnalysed = latest != null && (status === 'skipped' || (status === 'done' && triage == null))
    const skipRetryable = skipReason === '' || RETRYABLE_SKIPS.has(skipReason)

    const latestUid = String(latest?.uid ?? '')
    const proposalMessageUid = proposalMessage ? String(proposalMessage.uid ?? '') : latestUid
    // A proposal kept across a new inbound message is never mistaken for a
    // proposal ABOUT it: `triage-previous-message` fires whenever the row read
    // from is not `latest`'s own.
    const isPreviousMessage = proposalMessageUid !== latestUid
    // `Retriage` always targets `latest`: it is the message whose status is
    // outstanding or failed, whichever message the proposal shown reads from.
    const retriagePath = { orgCode: org, conversationUid: conversation.uid!, messageUid: latestUid }
    // `ReviewTriage` looks its row up by the row's OWN `message_uid`, which can
    // be an earlier message than `latest` while a new run is outstanding.
    const reviewPath = { orgCode: org, conversationUid: conversation.uid!, messageUid: proposalMessageUid }
    const busy = review.isPending || retriage.isPending
    // One run is outstanding; a second header click cannot add one.
    const headerBusy = busy || refreshing

    const decide = (decision: 'accepted' | 'rejected') => {
        // Accepting a proposal that carries a draft means accepting its answer
        // too, so the draft lands in the composer instead of asking for a
        // second click on *Use this draft*.
        //
        // The text applied is the RESPONSE's `resolved_draft_body`, not the
        // click-time copy of `draft_body`: the backend appends the sentence
        // naming the tickets this very review opened, and it can only know
        // them after the call (spec 2026-09-13-01). Falling back to the
        // click-time copy keeps a deployment whose backend predates the field
        // working, and `onChanged()`'s refetch is still not what is read —
        // the proposal this manager judged is the one that must be applied.
        //
        // `acknowledge` drafts nothing by design, so accepting one seeds
        // nothing — no empty composer, no confirmation dialog.
        const clicked =
            decision === 'accepted' && triage?.draft_body
                ? { subject: String(triage.draft_subject ?? ''), body: String(triage.draft_body) }
                : null

        review.mutate({ params: { path: reviewPath }, body: { decision } } as never, {
            onSuccess: (data: unknown) => {
                const resolved = data as
                    | { resolved_draft_subject?: string; resolved_draft_body?: string }
                    | undefined
                const draft =
                    decision === 'accepted' && resolved?.resolved_draft_body
                        ? {
                              subject: String(resolved.resolved_draft_subject ?? clicked?.subject ?? ''),
                              body: String(resolved.resolved_draft_body),
                          }
                        : clicked
                // Emptiness is re-checked at APPLY time, not at click time: a
                // manager who started typing while the request was in flight
                // must never be overwritten. That is also why this never opens
                // the replace dialog *Use this draft* uses — accepting is not
                // a request to discard what someone just wrote, so a composer
                // that holds text simply keeps it, and the draft stays one
                // click away in the card.
                if (draft && !composerApi.current?.hasText()) {
                    composerApi.current?.applyDraft(draft.subject, draft.body, { triageUid: triage?.uid })
                }
            },
        } as never)
    }
    const retry = () => retriage.mutate({ params: { path: retriagePath } } as never)

    // The one grey line of a message that was not analysed, and the re-run
    // when it can help. Rendered alone, or under a proposal kept from an
    // earlier message — the same block in both places, like the failed one.
    const skippedBlock = notAnalysed ? (
        <div className="space-y-1.5" data-testid="triage-skipped" data-reason={skipReason || undefined}>
            <p className="text-[11px] text-muted-foreground">
                {status === 'skipped' && KNOWN_SKIPS.has(skipReason)
                    ? t(`triage.skipped_${skipReason}`)
                    : t('triage.skipped')}
            </p>
            {skipRetryable && (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={busy || refreshing}
                    onClick={retry}
                    data-testid="triage-retry-skipped"
                >
                    <AppIcon path={mdiRestore} size="xs" className="mr-1" />
                    {t('triage.retry')}
                </Button>
            )}
        </div>
    ) : null

    const facts = (triage?.facts ?? []) as { id?: string; kind?: string; text?: string; source?: string }[]
    const factsUsed = (triage?.facts_used ?? []) as string[]
    const usedFacts = facts.filter((fact) => factsUsed.includes(String(fact.id)))
    const requests = (triage?.requests ?? []) as TriageRequest[]
    const outcome = String(triage?.outcome ?? '')
    const reviewed = triage != null && triage.status !== 'proposed'
    // What the card shows and what *Use this draft* copies is the RESOLVED
    // draft: the model's text plus the sentence naming the tickets the review
    // opened, which the backend rebuilds on every read. Before acceptance it
    // is the stored draft verbatim — there is no ticket to cite yet — so this
    // one expression is truthful on both sides of the click.
    const draftBody = String(triage?.resolved_draft_body ?? triage?.draft_body ?? '')
    const draftSubject = String(triage?.resolved_draft_subject ?? triage?.draft_subject ?? '')

    const applyDraft = (subject: string, body: string) => {
        if (composerApi.current?.hasText()) {
            setConfirmReplace({ subject, body })

            return
        }
        composerApi.current?.applyDraft(subject, body, { triageUid: triage?.uid })
    }

    return (
        <DetailSection
            sectionKey="triage"
            title={t('triage.title')}
            testId="conversation-triage-card"
            trailing={
                triage != null ? (
                    <button
                        type="button"
                        className="text-[11px] text-primary hover:underline flex-shrink-0 disabled:opacity-50"
                        disabled={headerBusy}
                        onClick={retry}
                        data-testid="triage-retry"
                    >
                        {t('triage.retry')}
                    </button>
                ) : undefined
            }
        >
            {/* A thread the resident has not written to yet: the analysis has
                nothing to read, and the card says so. */}
            {!latest && (
                <p className="text-[11px] text-muted-foreground" data-testid="triage-no-inbound">
                    {t('triage.no_inbound_yet')}
                </p>
            )}

            {/* `pending` is the only state with a spinner, and it means exactly
                one thing: a run is outstanding. Every terminal state — skipped,
                keyless — marks the message instead, so this never sticks
                forever. */}
            {!triage && refreshing && (
                <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="triage-pending">
                    <AppIcon path={mdiLoading} size="xs" className="motion-safe:animate-spin" aria-hidden />
                    {t('triage.analysing')}
                </p>
            )}

            {!triage && status === 'failed' && (
                <div className="space-y-1.5" data-testid="triage-failed">
                    <p className="text-[11px] text-muted-foreground">{t('triage.failed')}</p>
                    <Button variant="outline" size="sm" disabled={busy} onClick={retry} data-testid="triage-retry-failed">
                        <AppIcon path={mdiRestore} size="xs" className="mr-1" />
                        {t('triage.retry')}
                    </Button>
                </div>
            )}

            {!triage && skippedBlock}

            {triage != null && (
                <div className="space-y-2">
                    {/* A retriage keeps this proposal on screen instead of
                        replacing it with a bare spinner — the outcome, the
                        draft, the actions all stay put. This is the only sign
                        a new run is outstanding. */}
                    {refreshing && (
                        <p
                            className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
                            data-testid="triage-updating"
                        >
                            <AppIcon path={mdiLoading} size="xs" className="motion-safe:animate-spin" aria-hidden />
                            {t('triage.updating')}
                        </p>
                    )}

                    <div className="flex flex-wrap items-center gap-1.5">
                        <AppIcon path={mdiRobotOutline} size="xs" className="flex-shrink-0" aria-hidden />
                        <Badge
                            variant="outline"
                            className={`text-[10px] ${OUTCOME_TONE[outcome] ?? 'text-muted-foreground'}`}
                            data-testid="triage-outcome"
                        >
                            {t(`triage.outcome_${outcome}`, { defaultValue: outcome })}
                        </Badge>
                        {triage.urgency && (
                            <span
                                className={`text-[11px] ${URGENCY_TONE[String(triage.urgency)] ?? 'text-muted-foreground'}`}
                                data-testid="triage-urgency"
                            >
                                {t(`triage.urgency_${triage.urgency}`, { defaultValue: String(triage.urgency) })}
                            </span>
                        )}
                        {reviewed && (
                            <Badge variant="secondary" className="text-[10px]" data-testid="triage-decision">
                                {t(`triage.decision_${triage.status}`, { defaultValue: String(triage.status) })}
                            </Badge>
                        )}
                    </div>

                    {/* A proposal kept across a newer inbound message is never
                        left to be mistaken for a proposal about that message. */}
                    {isPreviousMessage && (
                        <p className="text-[11px] text-muted-foreground" data-testid="triage-previous-message">
                            {t('triage.previous_message')}
                        </p>
                    )}

                    {/* `latest`'s own run failed while an older proposal is
                        still shown above — the same failed block as the
                        no-proposal case, just alongside the kept proposal
                        instead of alone. */}
                    {!refreshing && status === 'failed' && (
                        <div className="space-y-1.5" data-testid="triage-failed">
                            <p className="text-[11px] text-muted-foreground">{t('triage.failed')}</p>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={busy}
                                onClick={retry}
                                data-testid="triage-retry-failed"
                            >
                                <AppIcon path={mdiRestore} size="xs" className="mr-1" />
                                {t('triage.retry')}
                            </Button>
                        </div>
                    )}

                    {/* `latest` was skipped while an older proposal is still
                        shown above: the reason goes under it, not in its place. */}
                    {!refreshing && skippedBlock}

                    {/* `escalate` shows no proposal at all — only that a human has
                        to read it. That is the safety exit doing its job, not a
                        degraded card. */}
                    {outcome === 'escalate' && (
                        <p className="text-[11px] text-muted-foreground" data-testid="triage-escalate-hint">
                            {t('triage.needs_a_human')}
                        </p>
                    )}

                    {/* `acknowledge` drafts nothing BY DESIGN, and Accept on it
                        closes the thread. Without this line a manager reads the
                        empty card as a drafter that failed and retries it — which
                        is exactly what spec 2026-09-12-04 observed. */}
                    {outcome === 'acknowledge' && (
                        <p className="text-[11px] text-muted-foreground" data-testid="triage-acknowledge-hint">
                            {t('triage.acknowledge_hint')}
                        </p>
                    )}

                    {requests.length > 0 && (
                        <ul className="space-y-1" data-testid="triage-requests">
                            {requests.map((request, index) => (
                                <li
                                    key={`${request.title ?? ''}-${index}`}
                                    className="rounded-md border bg-card px-2.5 py-2"
                                    data-testid="triage-request"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <AppIcon path={mdiTicketOutline} size="xs" className="flex-shrink-0" aria-hidden />
                                        <span className="text-sm text-foreground truncate">{request.title}</span>
                                    </div>
                                    {/* The ticket's body, shown in full: Accept
                                        writes it as the issue's description, and
                                        a manager cannot endorse text the card
                                        hides from them. */}
                                    {request.description && (
                                        <p
                                            className="mt-1 text-xs text-foreground whitespace-pre-wrap"
                                            data-testid="triage-request-description"
                                        >
                                            {request.description}
                                        </p>
                                    )}
                                    <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                                        {[
                                            request.subtype
                                                ? t(`promote.subtype_${request.subtype}`, { defaultValue: request.subtype })
                                                : '',
                                            request.trade_code ?? '',
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {usedFacts.length > 0 && (
                        <div className="space-y-1" data-testid="triage-facts">
                            <div className="text-[11px] text-muted-foreground">{t('triage.facts_used')}</div>
                            <ul className="space-y-0.5">
                                {usedFacts.map((fact) => (
                                    <li key={String(fact.id)} className="text-[11px] min-w-0" data-testid="triage-fact" data-kind={fact.kind}>
                                        {fact.kind === 'knowledge' && parseKnowledgeSource(String(fact.source ?? '')) ? (
                                            <div className="flex min-w-0 items-center gap-1">
                                                <KnowledgeFactLink
                                                    org={org}
                                                    source={parseKnowledgeSource(String(fact.source ?? ''))!}
                                                    text={String(fact.text ?? '')}
                                                />
                                                <NoteWrongButton
                                                    org={org}
                                                    source={parseKnowledgeSource(String(fact.source ?? ''))!}
                                                    factId={String(fact.id)}
                                                    text={String(fact.text ?? '')}
                                                />
                                            </div>
                                        ) : (
                                            <FactLink org={org} source={String(fact.source ?? '')}>
                                                {String(fact.text ?? '')}
                                            </FactLink>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* The draft: read-only here, and the ONLY home of an AI
                        draft. It reaches the composer on a click and never
                        before — from then on the text is the manager's. */}
                    {draftBody && (
                        <div className="rounded-md border bg-card p-2.5 space-y-1.5" data-testid="triage-draft">
                            <div className="text-[11px] text-muted-foreground">{t('triage.draft')}</div>
                            {draftSubject && (
                                <div className="text-xs font-medium text-foreground" data-testid="triage-draft-subject">
                                    {draftSubject}
                                </div>
                            )}
                            <p className="text-xs text-foreground whitespace-pre-wrap" data-testid="triage-draft-body">
                                {draftBody}
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => applyDraft(draftSubject, draftBody)}
                                data-testid="triage-use-draft"
                            >
                                <AppIcon path={mdiAutoFix} size="xs" className="mr-1" />
                                {t('triage.use_draft')}
                            </Button>
                        </div>
                    )}

                    {!reviewed && (
                        <div className="flex flex-wrap gap-1.5" data-testid="triage-actions">
                            {outcome !== 'escalate' && (
                                <>
                                    <Button
                                        size="sm"
                                        disabled={busy}
                                        onClick={() => decide('accepted')}
                                        data-testid="triage-accept"
                                    >
                                        <AppIcon path={mdiCheck} size="xs" className="mr-1" />
                                        {t('triage.accept')}
                                    </Button>
                                    {requests.length > 0 && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={busy}
                                            onClick={() =>
                                                onEditProposal({
                                                    // The thread's RESOLVED name, not the
                                                    // carrier's header (spec 2026-09-13-02).
                                                    title: String(requests[0].title ?? threadTitle(conversation, '')),
                                                    subtype: String(requests[0].subtype ?? 'request_intervention'),
                                                    assetUid: conversation.asset?.uid ? String(conversation.asset.uid) : '',
                                                    assetName: conversation.asset?.name
                                                        ? String(conversation.asset.name)
                                                        : '',
                                                    reviewMessageUid: proposalMessageUid,
                                                })
                                            }
                                            data-testid="triage-edit"
                                        >
                                            {t('triage.edit')}
                                        </Button>
                                    )}
                                </>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={busy}
                                onClick={() => decide('rejected')}
                                data-testid="triage-reject"
                            >
                                <AppIcon path={mdiClose} size="xs" className="mr-1" />
                                {t('triage.reject')}
                            </Button>
                        </div>
                    )}

                    {reviewed && triage.reviewed_at && (
                        <p className="text-[11px] text-muted-foreground" data-testid="triage-reviewed-by">
                            {t('triage.reviewed_by', {
                                who: triage.reviewed_by_name || t('triage.someone'),
                                when: formatDate(String(triage.reviewed_at), { dateStyle: 'short', timeStyle: 'short' }),
                            })}
                        </p>
                    )}

                    {error && (
                        <p className="text-[11px] text-destructive" data-testid="triage-error">
                            {error}
                        </p>
                    )}
                </div>
            )}

            {/* Replacing what a manager has typed is a destructive act, so it is
                asked — in an AlertDialog, never a native confirm(). */}
            <AlertDialog open={confirmReplace !== null} onOpenChange={(open) => !open && setConfirmReplace(null)}>
                <AlertDialogContent data-testid="triage-replace-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('triage.replace_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('triage.replace_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="triage-replace-cancel">{t('triage.replace_cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (confirmReplace) {
                                    composerApi.current?.applyDraft(confirmReplace.subject, confirmReplace.body, {
                                        triageUid: triage?.uid,
                                    })
                                }
                                setConfirmReplace(null)
                            }}
                            data-testid="triage-replace-confirm"
                        >
                            {t('triage.replace_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DetailSection>
    )
}

/**
 * A fact, linked to the row it came from.
 *
 * The source is an entity path (`assets/{uid}`, `issues/{uid}`,
 * `conversations/{uid}`) rather than a URL, because the console owns its own
 * routes — so the mapping lives here. An unrecognised kind prints the text
 * without a link instead of guessing a route.
 */
function FactLink({
    org,
    source,
    children,
}: {
    org: string
    source: string
    children: React.ReactNode
}) {
    const [prefix, uid] = source.split('/')
    const className = 'block truncate text-foreground hover:underline'

    if (prefix === 'assets' && uid) {
        return (
            <Link to="/$org/assets/$assetUid" params={{ org, assetUid: uid }} className={className}>
                <AppIcon path={SECTION_ICONS.assets} size="xs" className="inline mr-1" aria-hidden />
                {children}
            </Link>
        )
    }
    if (prefix === 'issues' && uid) {
        return (
            <Link to="/$org/issues/$uid" params={{ org, uid }} className={className}>
                <AppIcon path={mdiTicketOutline} size="xs" className="inline mr-1" aria-hidden />
                {children}
            </Link>
        )
    }
    if (prefix === 'conversations' && uid) {
        return (
            <Link
                to="/$org/conversations"
                params={{ org }}
                search={{
                    conversation: uid,
                    status: undefined,
                    channel: undefined,
                    resolution: undefined,
                    asset: undefined,
                    unattached: undefined,
                    draft: undefined,
                    q: undefined,
                    selected: undefined,
                    reply_for_issue: undefined,
                    message: undefined,
                }}
                className={className}
            >
                <AppIcon path={mdiMessageOutline} size="xs" className="inline mr-1" aria-hidden />
                {children}
            </Link>
        )
    }

    return <span className="block truncate text-muted-foreground">{children}</span>
}
